// client/src/context/auth-context.js - UPDATED with new domain
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext({
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  getSession: async () => {},
  refreshUser: async () => {}
});

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    token: localStorage.getItem('ilearn_token') || null,
    isLoading: true
  });

  useEffect(() => {
    // Initialize auth state
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('ilearn_token');
        
        if (token) {
          // Verify token validity
          const API_URL = process.env.REACT_APP_API_URL || 'https://api.lerenmotiveren.nl';
          const response = await fetch(`${API_URL}/api/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            
            setAuthState({
              isAuthenticated: !data.isAnonymous,
              user: data.user || null,
              token,
              isLoading: false
            });
          } else {
            // Token invalid
            localStorage.removeItem('ilearn_token');
            setAuthState({
              isAuthenticated: false,
              user: null,
              token: null,
              isLoading: false
            });
          }
        } else {
          // No token
          setAuthState(prev => ({
            ...prev,
            isLoading: false
          }));
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.removeItem('ilearn_token');
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          isLoading: false
        });
      }
    };

    initAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.lerenmotiveren.nl';
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Login failed');
      }
      
      const data = await response.json();
      
      localStorage.setItem('ilearn_token', data.token);
      
      setAuthState({
        isAuthenticated: true,
        user: data.user,
        token: data.token,
        isLoading: false
      });
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.lerenmotiveren.nl';
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Registration failed');
      }
      
      const data = await response.json();
      
      localStorage.setItem('ilearn_token', data.token);
      
      setAuthState({
        isAuthenticated: true,
        user: data.user,
        token: data.token,
        isLoading: false
      });
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('ilearn_token');
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false
    });
  };

  // Get session function for anonymous users
  const getSession = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.lerenmotiveren.nl';
      const response = await fetch(`${API_URL}/api/auth/session`, {
        headers: {
          'Authorization': `Bearer ${authState.token || ''}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to get session');
      
      const data = await response.json();
      
      // If token provided, update local storage
      if (data.token) {
        localStorage.setItem('ilearn_token', data.token);
        setAuthState(prev => ({
          ...prev,
          token: data.token
        }));
      }
      
      return data;
    } catch (error) {
      console.error('Session error:', error);
      return null;
    }
  };

  // Refresh user data from backend
  const refreshUser = async () => {
    if (!authState.token) return null;

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.lerenmotiveren.nl';
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${authState.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.user) {
          const updatedUser = data.user;
          setAuthState(prev => ({
            ...prev,
            user: updatedUser,
            isAuthenticated: !data.isAnonymous
          }));
          return updatedUser;
        }
      } else {
        // Token invalid, logout
        logout();
        return null;
      }
    } catch (error) {
      console.error('Refresh user error:', error);
      return null;
    }
  };

  const value = {
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    token: authState.token,
    isLoading: authState.isLoading,
    login,
    register,
    logout,
    getSession,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};